import React, { useState } from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
} from '@/components/shared/UISectionWrapper';
import UIIconButton from '@/components/shared/UIIconButton';
import { IProperty, IUnit, TenantLease } from '@/types';
import convertAddressToString from '@/utils/convertAddressToString';
import UIUnit from '@/components/shared/UIUnit';
import { TenancyProperty } from '@/components/shared/UITenancyOverwiev/UITenancyOverwiev';
import './UnitInformationSection.scss';

interface UnitInformationSectionProps {
  leases: TenantLease[];
  onViewUnitDetails?: (leaseId: number, unitId: number, propertyId: number) => void;
  onUnlinkTenant?: (leaseId: number) => void;
  onContactTenant?: (tenantId: number) => void;
}

const UnitInformationSection: React.FC<UnitInformationSectionProps> = ({
                                                                         leases,
                                                                         onViewUnitDetails = () => console.log('View unit details'),
                                                                         onUnlinkTenant = () => console.log('Unlink tenant'),
                                                                         onContactTenant = () => console.log('Contact tenant'),
                                                                       }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const CollapseButton = () => (
    <button
      className="UnitInformationSection__collapse-btn"
      onClick={toggleExpanded}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d={isExpanded ? 'M4 6L8 10L12 6' : 'M6 4L10 8L6 12'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  const mapLeaseToTenancyProperty = (lease: TenantLease): TenancyProperty => {
    const { unit, payments } = lease;
    const { property } = unit;

    const hasDebt = payments?.some((p) => p.status !== 'completed');

    return {
      id: String(property.id || 0),
      name: property.name || 'N/A',
      type: property.type,
      address: property.address
        ? convertAddressToString(property.address)
        : 'N/A',
      unit: unit.number || 'N/A',
      latitude: property.address?.latitude ?? null,
      longitude: property.address?.longitude ?? null,
      payment: {
        status: 'PAID',
        rental: '',
        debt: '',
        nextPayment: '',
        leaseExpiration: '',
        paymentMethod: '',
        warnings: 0,
        canPayOnline: false,
      },
      hasDebt,
      onPropertyInfo: (propertyId: string) => {
        if (unit.id && property.id) {
          onViewUnitDetails(lease.id, unit.id, property.id);
        }
      },
      onContactProperty: () => onContactTenant(lease.tenantId),
      onReportWarning: undefined,
      onMakePayment: undefined,
    };
  };

  if (!leases || leases.length === 0) {
    return null;
  }

  return (
    <UISectionWrapper className="UnitInformationSection">
      <UISectionHeader>
        <div className="UnitInformationSection__header">
          <h3 className="UnitInformationSection__title">
            Unit information {leases.length > 1 && `(${leases.length})`}
          </h3>
          <CollapseButton />
        </div>
        <div className="UnitInformationSection__divider" />
      </UISectionHeader>

      {isExpanded && (
        <UISectionBody>
          <div className="UnitInformationSection__leases-list">
            {leases.map((lease, index) => {
              const tenancyProperty = mapLeaseToTenancyProperty(lease);

              return (
                <div key={lease.id} className="UnitInformationSection__lease-item">
                  <UIUnit property={tenancyProperty} />

                  <div className="UnitInformationSection__unlink-wrapper">
                    <UIIconButton
                      iconType="unlink"
                      variant="danger"
                      size="medium"
                      text="Unlink tenant"
                      onClick={() => onUnlinkTenant(lease.id)}
                    />
                  </div>

                  {index < leases.length - 1 && (
                    <div className="UnitInformationSection__lease-divider" />
                  )}
                </div>
              );
            })}
          </div>
        </UISectionBody>
      )}
    </UISectionWrapper>
  );
};

export default UnitInformationSection;
